import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

// Impersonation stash keys. While "viewing as" another user, the admin's own
// tokens are parked here and the active accessToken is the impersonation token.
const ADMIN_ACCESS = 'adminAccessToken';
const ADMIN_REFRESH = 'adminRefreshToken';
const IMPERSONATOR = 'impersonator';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('accessToken'));
  const [loading, setLoading] = useState(true);
  // Derived from the stash so an impersonation session survives a page reload.
  const [impersonating, setImpersonating] = useState(() => !!localStorage.getItem(ADMIN_ACCESS));
  const [impersonator, setImpersonator] = useState(() => {
    try { return JSON.parse(localStorage.getItem(IMPERSONATOR) || 'null'); } catch { return null; }
  });

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setAccessToken(token);
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    setUser(data.user);
    setAccessToken(data.accessToken);
    return data.user;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try { await api.post('/auth/logout', { refreshToken }); } catch {}
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    // Never strand the admin stash on logout.
    localStorage.removeItem(ADMIN_ACCESS);
    localStorage.removeItem(ADMIN_REFRESH);
    localStorage.removeItem(IMPERSONATOR);
    setUser(null);
    setAccessToken(null);
    setImpersonating(false);
    setImpersonator(null);
  };

  // Start "viewing as" the given user. Parks the admin tokens, swaps in the
  // (non-refreshable) impersonation token, and re-scopes the whole app to the
  // target. WebSocket auto-reconnects because it keys off accessToken.
  const startImpersonation = async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    localStorage.setItem(ADMIN_ACCESS, localStorage.getItem('accessToken') || '');
    localStorage.setItem(ADMIN_REFRESH, localStorage.getItem('refreshToken') || '');
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.removeItem('refreshToken'); // impersonation tokens are not refreshable
    localStorage.setItem(IMPERSONATOR, JSON.stringify(data.impersonator || null));
    setUser(data.user);
    setAccessToken(data.accessToken);
    setImpersonating(true);
    setImpersonator(data.impersonator || null);
    return data.user;
  };

  // End impersonation and restore the admin session.
  const stopImpersonation = async () => {
    try { await api.post('/admin/impersonate/stop'); } catch {}
    const adminAccess = localStorage.getItem(ADMIN_ACCESS);
    const adminRefresh = localStorage.getItem(ADMIN_REFRESH);
    if (adminAccess) localStorage.setItem('accessToken', adminAccess); else localStorage.removeItem('accessToken');
    if (adminRefresh) localStorage.setItem('refreshToken', adminRefresh); else localStorage.removeItem('refreshToken');
    localStorage.removeItem(ADMIN_ACCESS);
    localStorage.removeItem(ADMIN_REFRESH);
    localStorage.removeItem(IMPERSONATOR);
    setImpersonating(false);
    setImpersonator(null);
    setAccessToken(adminAccess || null);
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{
      user, accessToken, loading, login, logout,
      impersonating, impersonator, startImpersonation, stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
